import React, { useEffect, useState } from 'react';
import { styled, alpha } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { observer } from 'mobx-react';
import {
  ChecklistOutlined,
  ContentCopy,
  DeleteForever,
  GetApp,
  HelpOutline,
  Notifications,
  PersonSearch,
  FileUpload,
  PeopleAlt,
} from '@mui/icons-material';
import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import { MassDeleteDialog } from './MassDeleteDialog';
import { SendNotificationUserDialog } from '../../components/SendNotificationUserDialog';
import LeadStore from '../../store/LeadStore';
import { DuplicateLeadDialog } from '../../components/DuplicateLeadDialog';
import { toJS } from 'mobx';
import { exportExcel } from '../../utils/exportFile';
import { MassStatusUpdateDialog } from '../../components/MassStatusUpdateDialog';
import AuthStore from '../../store/AuthStore';
import DocumentStore from '../../store/DocumentStore';
import LoadingProgressStore from '../../store/LoadingProgressStore';
import SnackbarStore from '../../store/SnackbarStore';
import { severity } from '../../utils/severity';
import { SendNotificationSubOwnerDialog } from '../../components/SendNotificationSubOwnerDialog/SendNotificationSubOwnerDialog';

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const ActionsMenu = observer(({ toggleLeadDetails, selectedPipeline, selectedLeads }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [bulkLeadsFiles, setBulkLeadsFiles] = useState([]);
  const [toggleSendNotificationDialog, setToggleSendNotificationDialog] = useState(false);
  const [toggleMassAssignDialog, setToggleMassAssignDialog] = useState(false);
  const [toggleMassStatusUpdateDialog, setToggleMassStatusUpdateDialog] = useState(false);
  const [toggleDuplicateLeadsDialog, setToggleDuplicateLeadsDialog] = useState(false);
  const [toggleMassDeleteDialog, setToggleMassDeletenDialog] = useState(false);
  const [toggleMassSubOwnerAssignDialog, setToggleMassSubOwnerAssignDialog] = useState(false);

  const handleMassSubOwnerAssignDialog = (show) => {
    setToggleMassSubOwnerAssignDialog(show);
  };

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleMassDeleteDialog = () => {
    setToggleMassDeletenDialog(!toggleMassDeleteDialog);
  };
  useEffect(() => {
    LeadStore.leadDuplicateStatus && handleClose();
  }, [LeadStore.leadDuplicateStatus]);

  const handleToggleNotifyUserDialog = (show) => {
    setToggleSendNotificationDialog(show);
  };

  const handleMassAssignDialog = (show) => {
    setToggleMassAssignDialog(show);
  };

  const handleToggleMassStatusUpdateDialog = () => {
    setToggleMassStatusUpdateDialog(!toggleMassStatusUpdateDialog);
  };

  const handleToggleDuplicateLeadsDialog = () => {
    setToggleDuplicateLeadsDialog(!toggleDuplicateLeadsDialog);
  };

  const handleExportLeads = () => {
    const currentPageData = toJS(LeadStore.leadsList);
    let excelData = currentPageData;
    if (selectedLeads.length) {
      excelData = [];
      selectedLeads.forEach((item) => {
        excelData.push(currentPageData.find((lead) => lead.id.toString() === item.toString()));
      });
    }

    exportExcel(excelData, toJS(LeadStore.leadsListColumns), 'LeadManagement');
  };

  const handleImportLeads = () => {
    document.querySelector('#upload-bulk-leads-file-input').click();
  };

  const handleImportLeadsChanged = (e) => {
    LoadingProgressStore.toggleIsLoading(true);
    const fileInfo = e.target.files[0];

    if (fileInfo.size > 30000000) {
      SnackbarStore.toggleSnackbar(true, severity.error, 'File size is too large, The max file size is 30 MB ');
      LoadingProgressStore.toggleIsLoading(false);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async (event) => {
      setBulkLeadsFiles([
        {
          file: fileInfo,
          fileBinary: event.target.result,
        },
      ]);
    };
    reader.readAsDataURL(fileInfo);
  };

  useEffect(() => {
    LoadingProgressStore.toggleIsLoading(false);
    DocumentStore.uploadBulkLeadsStatus && LeadStore.setRefreshLeadersBoard();
    setBulkLeadsFiles([]);
  }, [DocumentStore.uploadBulkLeadsStatus]);

  useEffect(() => {
    if (bulkLeadsFiles.length) {
      const payload = {
        pipeLineId: selectedPipeline?.id || selectedPipeline,
        userId: AuthStore.userDetails.Id,
        leadId: 0,
        files: [],
        documentType: 'update-bulk-leads',
      };

      const formData = new FormData();
      bulkLeadsFiles.forEach((item) => {
        formData.append('Files', item.file);
      });
      payload.files = formData;

      DocumentStore.uploadBulkLeads(payload);
    }
  }, [bulkLeadsFiles]);

  return (
    <div>
      <Tooltip title="Please select leads to make action">
        <span>
          <Button
            id="demo-customized-button"
            aria-controls={open ? 'demo-customized-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            variant="outlined"
            disabled={toggleLeadDetails !== null}
            disableElevation
            sx={{
              color: '#000',
              borderColor: '#f07b0e',
              '&:hover': {
                backgroundColor: '#f1f1f1',
                borderColor: '#f07b0e',
              },
              height: '34px',
            }}
            onClick={handleClick}
            endIcon={<KeyboardArrowDownIcon />}
          >
            Actions
          </Button>
        </span>
      </Tooltip>
      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{
          'aria-labelledby': 'demo-customized-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {AuthStore.userDetails.Role.toUpperCase() === 'SUAD' ? (
          <MenuItem onClick={() => handleToggleMassDeleteDialog()} disableRipple disabled={selectedLeads.length === 0}>
            <DeleteForever />
            Mass Delete //
          </MenuItem>
        ) : (
          ''
        )}
        <MenuItem onClick={handleToggleDuplicateLeadsDialog} disableRipple disabled={selectedLeads.length === 0}>
          <ContentCopy />
          Duplicate Leads
        </MenuItem>
        <MenuItem
          onClick={() => handleToggleNotifyUserDialog(true)}
          disableRipple
          disabled={selectedLeads.length === 0}
        >
          <Notifications />
          Notify Status
        </MenuItem>
        {AuthStore.userDetails.Role.toUpperCase() === 'SUAD' ? (
          <MenuItem onClick={() => handleMassAssignDialog(true)} disableRipple disabled={selectedLeads.length === 0}>
            <PeopleAlt />
            Mass Assign
          </MenuItem>
        ) : (
          ''
        )}
        {AuthStore.userDetails.Role.toUpperCase() === 'SUAD' ? (
          <MenuItem
            onClick={() => handleMassSubOwnerAssignDialog(true)}
            disableRipple
            disabled={selectedLeads.length === 0}
          >
            <PeopleAlt />
            Mass Sub Owner Assign
          </MenuItem>
        ) : (
          ''
        )}
        <MenuItem onClick={handleToggleMassStatusUpdateDialog} disableRipple disabled={selectedLeads.length === 0}>
          <ChecklistOutlined />
          Mass Status Update
        </MenuItem>
        <MenuItem onClick={handleExportLeads} disableRipple>
          <GetApp />
          Export Leads
        </MenuItem>
        {AuthStore.userDetails.Role.toUpperCase() === 'SUAD' ? (
          <MenuItem onClick={handleImportLeads} disableRipple>
            <FileUpload />
            Import Leads
            <VisuallyHiddenInput
              id="upload-bulk-leads-file-input"
              type="file"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={handleImportLeadsChanged}
            />
          </MenuItem>
        ) : (
          ''
        )}
      </StyledMenu>
      <MassDeleteDialog
        toggleMassDeleteDialog={toggleMassDeleteDialog}
        handleToggleMassDeleteDialog={handleToggleMassDeleteDialog}
        selectedLeads={selectedLeads}
      ></MassDeleteDialog>
      <SendNotificationUserDialog
        notifyLeads={true}
        headerLabel="Notify Leads"
        toggleSendNotificationDialog={toggleSendNotificationDialog}
        handleToggleNotifyUserDialog={handleToggleNotifyUserDialog}
        selectedLeads={selectedLeads}
        refreshLeadersBoard={false}
      ></SendNotificationUserDialog>
      <SendNotificationSubOwnerDialog
        headerLabel="Mass Sub Owner Assign"
        toggleSendNotificationDialog={toggleMassSubOwnerAssignDialog}
        handleToggleNotifyUserDialog={handleMassSubOwnerAssignDialog}
        selectedLeads={selectedLeads}
        refreshLeadersBoard={true}
      />
      <SendNotificationUserDialog
        notifyLeads={false}
        headerLabel="Mass Leads Assign"
        toggleSendNotificationDialog={toggleMassAssignDialog}
        handleToggleNotifyUserDialog={handleMassAssignDialog}
        selectedLeads={selectedLeads}
        refreshLeadersBoard={true}
      ></SendNotificationUserDialog>
      <MassStatusUpdateDialog
        toggleMassStatusUpdateDialog={toggleMassStatusUpdateDialog}
        handleToggleMassStatusUpdateDialog={handleToggleMassStatusUpdateDialog}
        selectedLeads={selectedLeads}
      ></MassStatusUpdateDialog>
      <DuplicateLeadDialog
        toggleDuplicateLeadsDialog={toggleDuplicateLeadsDialog}
        selectedPipeline={selectedPipeline}
        handleToggleDuplicateLeadsDialog={handleToggleDuplicateLeadsDialog}
        selectedLeads={selectedLeads}
      ></DuplicateLeadDialog>
    </div>
  );
});
